import React, { useRef, useEffect, useState } from 'react';
import { Box, LinearProgress } from '@/ui';
import BottomTabBar from '@/components/BottomTabBar';
import Footer from '@/components/on-boarding/Footer';
import ScrollPill from '@/components/ScrollPill';
import { OuterNavChips, InnerNavMobile, SectionWarnings } from './';
import type { ResolvedWarning, WarningToken } from '@/shared/warnings/types';

interface Route {
  key: string;
  label: string;
}

interface MobileLayoutProps {
  outerRoutes: Route[];
  selectedOuter: string;
  currentInnerRoutes: Route[];
  selectedInner: string;
  innerNavMenuAnchor: HTMLElement | null;
  sectionWarnings: ResolvedWarning[];
  nextButtonLabel: string;
  currentFormId: string;
  nextButtonLoading: boolean;
  nextButtonDisabled: boolean;
  isNavigating: boolean;
  onOuterRouteChange: (routeKey: string) => Promise<void>;
  onInnerNavMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onInnerNavMenuClose: () => void;
  onInnerNavMenuItemClick: (routeKey: string) => Promise<void>;
  onNavigateToInner: (key: string) => void;
  onSectionTokenClick: (token: WarningToken) => Promise<void>;
  onBackNav: () => Promise<void>;
  renderBudgetForm: () => React.ReactNode;
  /** When true, transforms "council tax" to "council rates" for NI users */
  isNorthernIreland?: boolean;
}

const MobileLayout: React.FC<MobileLayoutProps> = ({
  outerRoutes,
  selectedOuter,
  currentInnerRoutes,
  selectedInner,
  innerNavMenuAnchor,
  sectionWarnings,
  nextButtonLabel,
  currentFormId,
  nextButtonLoading,
  nextButtonDisabled,
  isNavigating,
  onOuterRouteChange,
  onInnerNavMenuOpen,
  onInnerNavMenuClose,
  onInnerNavMenuItemClick,
  onNavigateToInner,
  onSectionTokenClick,
  onBackNav,
  renderBudgetForm,
  isNorthernIreland = false,
}) => {
  const mobileContentRef = useRef<HTMLDivElement | null>(null);
  
  // Delayed loading indicator - only show after 150ms to avoid flashing
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (isNavigating) {
      timeoutId = setTimeout(() => setShowLoadingIndicator(true), 150);
    } else {
      setShowLoadingIndicator(false);
    }
    return () => clearTimeout(timeoutId);
  }, [isNavigating]);

  return (
    <Box sx={{
      display: { xs: 'flex', md: 'none' },
      height: '100dvh',
      width: '100vw',
      backgroundColor: '#f5f5f5',
      flexDirection: 'column',
      position: 'relative'
    }}>
      {/* Content with padding for footer */}
      <Box 
        ref={mobileContentRef}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          backgroundColor: '#f7f6f2',
          px: 2,
          pt: -1,
          pb: 'calc(56px + env(safe-area-inset-bottom))', // Space for BottomTabBar only
          overflow: 'auto',
          overflowX: 'hidden',
          position: 'relative',
          // Smooth momentum scrolling on iOS
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Top Navigation - Outer Routes */}
        <Box sx={{ mb: 1 }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexWrap: 'nowrap',
            overflowX: 'auto',
            mb: 1,
            pb: 0.5,
            px: 0.5,
            pt: 2,
            // Smooth momentum scrolling on iOS
            WebkitOverflowScrolling: 'touch',
            // Hide scrollbar while keeping scroll functionality
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE and Edge
            '&::-webkit-scrollbar': {
              display: 'none' // Chrome, Safari, Opera
            }
          }}>
            <OuterNavChips 
              routes={outerRoutes} 
              selectedKey={selectedOuter} 
              onSelect={onOuterRouteChange} 
            />
          </Box>
        </Box>

        {/* Second Navigation - Inner Routes - Sticky */}
        <Box sx={{ 
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: '#f7f6f2',
          mx: -2,
          px: 2,
          pt: 0.5,
          pb: 1,
          mb: 0.5,
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            gap: 1.5,
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden'
          }}>
            <InnerNavMobile
              routes={currentInnerRoutes}
              selectedKey={selectedInner}
              onSelect={onNavigateToInner}
              menuAnchor={innerNavMenuAnchor}
              onOpenMenu={onInnerNavMenuOpen}
              onCloseMenu={onInnerNavMenuClose}
              onSelectFromMenu={onInnerNavMenuItemClick}
            />
          </Box>
        </Box>

        {/* Main Content - with smooth transition */}
        <Box sx={{ 
          flex: 1, 
          pb: 2,
          opacity: isNavigating ? 0.5 : 1,
          transform: isNavigating ? 'translateY(4px)' : 'translateY(0)',
          transition: 'opacity 0.15s ease-out, transform 0.15s ease-out',
          willChange: isNavigating ? 'opacity, transform' : 'auto',
        }}>
          {/* Section-level Warning Panel (above form) */}
          <SectionWarnings
            warnings={sectionWarnings}
            onTokenClick={onSectionTokenClick}
            isNorthernIreland={isNorthernIreland}
          />
          {renderBudgetForm()}
        </Box>

        {/* Footer for Mobile - At bottom of content */}
        <Box sx={{
          mt: 'auto',
          borderTop: '1px solid #e0e0e0',
          mb: 8
        }}>
          <Footer
            handleBack={onBackNav}
            shouldApplyTopBorder={false}
            shouldApplyBackgroundColor={false}
            shouldBackDisabled={false}
            nextButtonText={nextButtonLabel}
            formId={currentFormId}
            nextButtonLoading={nextButtonLoading}
            nextButtonDisabled={nextButtonDisabled}
            showSecondaryButton={false}
          />
        </Box>
      </Box>

      {/* Scroll Pill - contextual navigation helper for fast scrolling */}
      <ScrollPill
        scrollContainerRef={mobileContentRef}
        navVisible={true} // BottomTabBar is always visible in this layout
        autoHideDelay={1600} // was 800ms; keep visible longer after scroll
        navBottomOffsetPx={112} // push pill slightly higher so it doesn't sit behind the tab bar
        showAtTop
        showAtBottom
        boundaryHoldDelay={1600}
      />

      {/* Bottom Tab Bar for Mobile */}
      <BottomTabBar />

      {/* Subtle top progress bar for Navigation - only shown after 150ms delay */}
      {showLoadingIndicator && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            zIndex: 9999,
            backgroundColor: 'rgba(21, 95, 84, 0.15)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#155f54',
            },
          }}
        />
      )}
    </Box>
  );
};

export default MobileLayout;

